/**
 * The hotel checkout form: a 4-step accordion (your details → room
 * preferences → special requests → payment) that composes the submission
 * payload and calls `hotelCheckoutState.submit`.
 */

import type { RoomOptionModel as RoomOption } from '@elara/client-core/models/RoomOptionModel';
import type { HotelPaymentSelection } from '@elara/domain/flows';
import type { HotelBookingRoom } from '@elara/domain/models/schemas/booking-schemas';

import type { SectionId } from './FormControls.js';
import type { CheckoutSnapshot, HotelCheckoutFormState } from './types.js';

import { parseISO } from 'date-fns';
import React, { useState } from 'react';

import { bookingOccupancyToRateOccupancy } from '@elara/client-core/lib/occupancy-utils';
import { formatPrice } from '@elara/client-core/lib/utils';
import { combinePaymentSchedules, summarizePaymentSchedule } from '@elara/domain/payment-schedule';

import { useAppStates } from '#states/hooks.js';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  FloatingInput,
  FloatingSelect,
  FloatingTextarea,
  SelectItem,
} from '#ui/index.js';
import { PhoneInput } from '#ui/PhoneInput.js';

import { CheckoutCardSelector } from './CheckoutCardSelector.js';
import { COUNTRY_OPTIONS, defaultRoomFormEntry, formatAddressLine, sortRoomsByName } from './form-state.js';
import { CollapsibleFormSection } from './FormControls.js';
import { RoomFormSection } from './RoomFormSection.js';

import { CheckoutPolicyDetails } from '../CheckoutPolicyDetails.js';
import { getPaymentScheduleForRoom } from '../payment-schedule-utils.js';

export function DraftForm({
  snapshot,
  tripId,
  roomOptionsById,
  form,
  setForm,
  onSubmitted,
}: {
  snapshot: CheckoutSnapshot;
  tripId: string;
  roomOptionsById: Map<string, RoomOption | undefined>;
  form: HotelCheckoutFormState;
  setForm: React.Dispatch<React.SetStateAction<HotelCheckoutFormState>>;
  onSubmitted: () => void;
}): React.ReactNode {
  const { hotelCheckoutState } = useAppStates('hotelCheckoutState');
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  // Payment is ephemeral (a saved-card id or a one-off vault token) and lives outside the persisted
  // form draft. The booking can't be submitted until the traveler picks or enters a card.
  const [payment, setPayment] = useState<HotelPaymentSelection | undefined>(undefined);

  // Server-resolved eligible extras per room, keyed by itinerary item id. The
  // server already collapsed crib/rollaway eligibility + caps into this list, so
  // the form just renders it — no client-side occupancy math.
  const eligibleExtrasByItem = new Map(snapshot.eligibleExtras.map((e) => [e.itineraryItemId, e.extras] as const));

  const requiredFieldsOk =
    form.leadGuest.firstName.trim() &&
    form.leadGuest.lastName.trim() &&
    form.leadGuest.email.trim() &&
    form.leadGuest.phone.trim() &&
    form.address.street.trim() &&
    form.address.city.trim() &&
    form.address.postalCode.trim() &&
    form.address.countryCode.trim();
  const canSubmit = !isSaving && Boolean(requiredFieldsOk) && snapshot.rooms.length > 0 && payment !== undefined;

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsSaving(true);
    setSubmitError(undefined);
    try {
      // Compose the full per-room state from the prepared room shape +
      // user's form patches (extras, prefs, note). Forwarded to the server
      // which re-runs PriceCheck and creates the doc on success.
      const rooms: HotelBookingRoom[] = snapshot.rooms.map((room) => {
        const r = form.rooms[room.itineraryItemId] ?? defaultRoomFormEntry();
        const eligible = eligibleExtrasByItem.get(room.itineraryItemId) ?? [];
        const selectedExtras = Object.entries(r.extras)
          .filter(([, qty]) => qty > 0)
          .map(([extraId, quantity]) => {
            const catalogEntry = eligible.find((e) => e.id === extraId);
            return { extraId, sabreCode: catalogEntry?.sabreRoomExtraCode, quantity };
          });
        return {
          ...room,
          selectedExtras,
          ...(r.bedTypePreference !== undefined ? { bedTypePreference: r.bedTypePreference } : {}),
          ...(r.smokingPreference !== undefined ? { smokingPreference: r.smokingPreference } : {}),
          ...(r.accessibilityNeeds !== undefined ? { accessibilityNeeds: r.accessibilityNeeds } : {}),
        };
      });

      if (!payment) return;
      const result = await hotelCheckoutState.submit({
        leadGuest: form.leadGuest,
        address: form.address,
        customerRequest: form.customerRequest.trim() || undefined,
        rooms,
        payment,
      });
      if (result.status === 'submitted') {
        onSubmitted();
      }
      // On `price_changed` the state replaces prepData; the page re-renders
      // automatically with the refreshed `priceCheckResult`, so the issues
      // banner appears and the submit button blocks until cleared.
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit booking');
    } finally {
      setIsSaving(false);
    }
  }

  const orderedRooms = sortRoomsByName(snapshot.rooms, roomOptionsById);

  // Trip-level payment schedule → what's charged on submit (`dueNow`) vs collected later. Drives the
  // pay-vs-hold disclosure, the submit-button label, and (charge rates) the CVV requirement.
  const roomRateInfos = snapshot.rooms.map((room) => ({
    room,
    roomName: roomOptionsById.get(room.roomOptionId)?.displayName,
    rate: roomOptionsById
      .get(room.roomOptionId)
      ?.getRateByIdentity({
        productCode: room.productCode,
        occupancy: bookingOccupancyToRateOccupancy(room.occupancy),
      }),
    priceSnapshot: snapshot.priceSnapshot.find((s) => s.itineraryItemId === room.itineraryItemId),
  }));
  const { dueNow, dueLater } = summarizePaymentSchedule(
    combinePaymentSchedules(
      roomRateInfos.map(({ rate, priceSnapshot }) =>
        getPaymentScheduleForRoom({
          rate,
          priceSnapshot,
          checkInDate: snapshot.checkInDate,
          checkOutDate: snapshot.checkOutDate,
        }),
      ),
    ),
  );
  const anyNonRefundable = roomRateInfos.some(({ rate }) => rate?.refundable === false);
  // A submit that found the price moved re-priced the page in place; say so where the click happens.
  const priceChanged = hotelCheckoutState.priceChanges.length > 0;

  // Accordion state: only one section is open at a time. Starts on the
  // first section so the user sees a clear next step on landing.
  const [openSection, setOpenSection] = useState<SectionId>('your-details');

  const yourDetailsSummary = requiredFieldsOk ? (
    <div className="space-y-0.5">
      <div className="flex items-center gap-3">
        <span>{`${form.leadGuest.firstName} ${form.leadGuest.lastName}`.trim()}</span>
        <span>{form.leadGuest.email}</span>
        <span>{form.leadGuest.phone}</span>
      </div>
      <p>{formatAddressLine(form.address)}</p>
    </div>
  ) : (
    'Add your name, contact, and billing address.'
  );

  const roomPrefsSummary =
    orderedRooms.length === 0 ? (
      'No rooms to configure'
    ) : (
      <ul className="space-y-0.5">
        {orderedRooms.map((room) => {
          const roomOption = roomOptionsById.get(room.roomOptionId);
          const eligible = eligibleExtrasByItem.get(room.itineraryItemId) ?? [];
          const roomState = form.rooms[room.itineraryItemId] ?? defaultRoomFormEntry();
          const bed = roomOption?.bedTypes.find((b) => b.sabreBedTypeCode === roomState.bedTypePreference);
          const bedLabel = bed ? (bed.count > 1 ? `${bed.count} × ${bed.description}` : bed.description) : undefined;
          const extras = Object.entries(roomState.extras)
            .filter(([, qty]) => qty > 0)
            .map(([extraId, qty]) => {
              const catalogEntry = eligible.find((e) => e.id === extraId);
              const name = catalogEntry?.name ?? extraId;
              return qty > 1 ? `${name} (×${qty})` : name;
            });
          const parts = [bedLabel, extras.length > 0 ? extras.join(', ') : undefined].filter(Boolean);
          return (
            <li key={room.itineraryItemId} className="truncate">
              <span>{roomOption?.displayName ?? 'Room'} with </span>
              {parts.length > 0 ? `${parts.join(' and ')}` : ''}
            </li>
          );
        })}
      </ul>
    );

  const customerRequest = form.customerRequest.trim();
  const specialRequestsSummary = customerRequest ? customerRequest : undefined;

  const paymentSummary = payment
    ? dueNow
      ? `${formatPrice(dueNow.amount, dueNow.currency)} charged on submit`
      : 'No charge today — card hold'
    : 'Add or select a card to pay.';

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-xl md:text-2xl font-display">Confirm your booking</h2>
        <p className="mt-1.5 text-sm text-pretty text-muted-foreground">
          Review your details, choose how to pay, and confirm. We’ll secure the rooms with the hotel and send your
          confirmation.
        </p>
      </section>
      <CollapsibleFormSection
        title="1. Your details"
        summary={yourDetailsSummary}
        complete={!!requiredFieldsOk}
        isOpen={openSection === 'your-details'}
        onEdit={() => setOpenSection('your-details')}
        footer={
          <Button onClick={() => setOpenSection('room-prefs')} disabled={!requiredFieldsOk}>
            Next
          </Button>
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FloatingInput
              label="First name"
              required
              value={form.leadGuest.firstName}
              onChange={(e) => setForm({ ...form, leadGuest: { ...form.leadGuest, firstName: e.target.value } })}
            />
            <FloatingInput
              label="Last name"
              required
              value={form.leadGuest.lastName}
              onChange={(e) => setForm({ ...form, leadGuest: { ...form.leadGuest, lastName: e.target.value } })}
            />
            <FloatingInput
              label="Email"
              required
              type="email"
              value={form.leadGuest.email}
              onChange={(e) => setForm({ ...form, leadGuest: { ...form.leadGuest, email: e.target.value } })}
            />
            <PhoneInput
              value={form.leadGuest.phone}
              onChange={(phone) => setForm({ ...form, leadGuest: { ...form.leadGuest, phone } })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <h2 className="md:col-span-2 font-display">Address</h2>
            <FloatingInput
              label="Street"
              required
              value={form.address.street}
              onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })}
            />
            <FloatingInput
              label="City"
              required
              value={form.address.city}
              onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
            />
            <FloatingInput
              label="Postal code"
              required
              value={form.address.postalCode}
              onChange={(e) => setForm({ ...form, address: { ...form.address, postalCode: e.target.value } })}
            />
            <FloatingSelect
              label="Country"
              required
              value={form.address.countryCode}
              onValueChange={(value) => setForm({ ...form, address: { ...form.address, countryCode: value } })}
            >
              {COUNTRY_OPTIONS.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </FloatingSelect>
          </div>
        </div>
      </CollapsibleFormSection>

      {orderedRooms.length > 0 ? (
        <CollapsibleFormSection
          title="2. Room preferences"
          summary={roomPrefsSummary}
          complete={openSection === 'special-requests' || openSection === 'payment'}
          isOpen={openSection === 'room-prefs'}
          onEdit={() => setOpenSection('room-prefs')}
          footer={<Button onClick={() => setOpenSection('special-requests')}>Next</Button>}
        >
          <div className="space-y-6">
            {orderedRooms.map((room, index) => {
              const roomOption = roomOptionsById.get(room.roomOptionId);
              const roomState = form.rooms[room.itineraryItemId] ?? defaultRoomFormEntry();
              const baseTitle = roomOption?.displayName ?? 'Room';
              const counterLabel = orderedRooms.length > 1 ? `Room ${index + 1} of ${orderedRooms.length}` : undefined;
              return (
                <React.Fragment key={room.itineraryItemId}>
                  <RoomFormSection
                    title={baseTitle}
                    counterLabel={counterLabel}
                    occupancy={room.occupancy}
                    bedTypes={roomOption?.bedTypes ?? []}
                    state={roomState}
                    thumbnailUrl={roomOption?.images?.[0]?.url}
                    roomSize={roomOption?.size}
                    showBedPreferenceNote={index === orderedRooms.length - 1}
                    extras={eligibleExtrasByItem.get(room.itineraryItemId) ?? []}
                    onChange={(patch) =>
                      setForm((prev) => ({
                        ...prev,
                        rooms: { ...prev.rooms, [room.itineraryItemId]: { ...roomState, ...patch } },
                      }))
                    }
                  />
                  {index < orderedRooms.length - 1 ? <hr className="-mr-6" /> : null}
                </React.Fragment>
              );
            })}
          </div>
        </CollapsibleFormSection>
      ) : null}

      <CollapsibleFormSection
        title="3. Special requests"
        summary={specialRequestsSummary}
        complete={openSection === 'payment'}
        isOpen={openSection === 'special-requests'}
        onEdit={() => setOpenSection('special-requests')}
        footer={<Button onClick={() => setOpenSection('payment')}>Next</Button>}
      >
        <FloatingTextarea
          id="customerRequest"
          label="Anything else the hotel should know? (optional)"
          value={form.customerRequest}
          onChange={(e) => setForm({ ...form, customerRequest: e.target.value })}
          rows={3}
        />
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title="4. Payment"
        summary={paymentSummary}
        complete={payment !== undefined}
        isOpen={openSection === 'payment'}
        onEdit={() => setOpenSection('payment')}
      >
        {/* Pay-vs-hold disclosure — make it unmistakable whether the card is charged on submit. */}
        {dueNow ? (
          <Alert variant="warning" className="mb-4">
            <AlertTitle>You’ll be charged {formatPrice(dueNow.amount, dueNow.currency)} today</AlertTitle>
            <AlertDescription>
              {anyNonRefundable ? 'This rate is non-refundable — payment is taken in full when you confirm. ' : ''}
              {dueLater ? `${formatPrice(dueLater.amount, dueLater.currency)} is collected at the property.` : ''}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="info" className="mb-4">
            <AlertTitle>No charge today</AlertTitle>
            <AlertDescription>
              Your card guarantees the booking — the hotel collects
              {dueLater ? ` ${formatPrice(dueLater.amount, dueLater.currency)}` : ' payment'} at your stay.
            </AlertDescription>
          </Alert>
        )}
        <CheckoutCardSelector
          value={payment}
          onChange={setPayment}
          bookingThrough={parseISO(snapshot.checkOutDate)}
        />

        {/* Full cancellation / refund / terms shown INLINE (not behind the "Full policies" link) so the
            binding terms are 100% visible before the guest commits (ELARA-829). */}
        {roomRateInfos.some(({ rate }) => rate) ? (
          <div className="mt-6 rounded-lg border border-border bg-muted/40 p-4">
            <h4 className="text-sm font-semibold">Cancellation &amp; terms</h4>
            <div className="mt-3 space-y-5">
              {roomRateInfos.map(({ room, rate, roomName }) =>
                rate ? (
                  <div key={room.itineraryItemId}>
                    {roomRateInfos.length > 1 && roomName ? (
                      <p className="mb-1 text-xs font-medium text-muted-foreground">{roomName}</p>
                    ) : null}
                    <CheckoutPolicyDetails rate={rate} checkInDate={snapshot.checkInDate} />
                  </div>
                ) : null,
              )}
            </div>
          </div>
        ) : null}
      </CollapsibleFormSection>

      {openSection === 'payment' ? (
        <>
          {priceChanged ? (
            <Alert variant="warning">
              <AlertTitle>The price has changed</AlertTitle>
              <AlertDescription>
                The hotel re-priced this stay while you were booking. The total and the amount due today now show the
                new price. Confirm below to book at that price.
              </AlertDescription>
            </Alert>
          ) : null}
          {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
          <div className="space-y-3 pt-2">
            <Button className="w-full" size="lg" onClick={handleSubmit} disabled={!canSubmit} isBusy={isSaving}>
              {dueNow ? `Pay ${formatPrice(dueNow.amount, dueNow.currency)} & book` : 'Confirm booking'}
            </Button>
            <p className="text-center text-xs text-muted-foreground text-pretty">
              By completing this booking, you agree to the terms of the hotel and Elara&apos;s Terms &amp; Conditions.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}
